import apiClient from './index';

export type DeviceInDB = {
  id: string;
  corps_id: string;
  building_id: string;
  status: 'ok' | 'warn' | 'error' | 'NEW' | 'REPLACED';
  house_id?: number;            // "id дома" из вашей доменной модели
  room_number?: number;         // № помещения
  manufacturer?: string;
  system_type?: 'ХВС' | 'ГВС' | 'Отопление' | 'Электросчетчик' | 'УРТ';
  suffix?: string;              // суффикс
  meter_type?: 'ХВС' | 'ГВС' | 'Отопление' | 'Электросчетчик' | string;
  battery?: number | null;      // заряд %
  model?: string;               // модель/линейка
  incidents?: string[];         // список инцидентов (короткие коды)
  serial_number?: string;          // заводской номер
  seal_date?: string | null;
  verification_date?: string | null;
  last_data?: number | null; // показания
  last_seen_at?: string | null; // время прихода показаний (ISO)
  comment?: string | null;      // комментарий
  history_value?: number | null; // показание за выбранный день
  history_ts?: string | null;    // время показания за день
  history_components?: Record<string, number | null> | null;
  description?: string | null;
  dev_eui?: string | null;
  mqtt_topic?: string | null;
  reading_collection_mode?: 'automatic' | 'file_import' | null;
  lorawan_enabled?: boolean | null;
  lorawan_sync_status?: string | null;
  lorawan_last_sync_at?: string | null;
  lorawan_sync_error?: string | null;
  lorawan_signal_rssi?: number | null;
  lorawan_signal_snr?: number | null;
  lorawan_gateways_count?: number | null;
  heat_coefficient?: number | null;
  heat_reduction_coefficient?: number | null;
  heat_is_direct?: boolean | null;
  heat_notes?: string | null;
  corp_name?: string | null;
  building_name?: string | null;
  apartment_id?: string | null;
  apartment_number?: string | number;
  rates?: Array<{ code: string; label?: string; value: number | null }>;
  latest_components?: Record<string, number | null> | null;
  heat_installation_date?: string | null;
  heat_removal_date?: string | null;
  heat_seal_status?: string | null;
  heat_operational_status?: string | null;
  heat_parent_device_id?: string | null;
  heat_archived?: boolean | null;
};

export type DeviceCreate = {
  corps_id: string;
  apartment_id: string;
  building_id?: string;
  room_number?: number;
  manufacturer?: string;
  suffix?: string;
  meter_type?: string;
  model?: string;
  serial_number?: string;
  seal_date?: string;
  verification_date?: string;
  comment?: string;
  description?: string;
  system_type?: string;
  dev_eui?: string;
  mqtt_topic?: string;
  reading_collection_mode?: 'automatic' | 'file_import';
  lorawan_enabled?: boolean;
  heat_coefficient?: number;
  heat_reduction_coefficient?: number;
  heat_is_direct?: boolean;
  heat_notes?: string;
  heat_installation_date?: string;
  heat_removal_date?: string;
  heat_seal_status?: string;
  heat_operational_status?: string;
  heat_parent_device_id?: string;
  heat_archived?: boolean;
  radiator_catalog_id?: string;
  radiator_model_name?: string;
  radiator_manufacturer?: string;
  radiator_placement?: string;
  radiator_room_label?: string;
  radiator_section_count?: number;
  radiator_power_watts?: number;
  radiator_height_mm?: number;
  radiator_width_mm?: number;
  radiator_depth_mm?: number;
  radiator_is_corner?: boolean;
  radiator_notes?: string;
};

export type DeviceUpdate = {
  manufacturer?: string | null;
  system_type?: string | null;
  serial_number?: string | null;
  seal_date?: string | null;
  verification_date?: string | null;
  comment?: string | null;
  description?: string | null;
  dev_eui?: string | null;
  mqtt_topic?: string | null;
  reading_collection_mode?: 'automatic' | 'file_import' | null;
  lorawan_enabled?: boolean | null;
  heat_coefficient?: number | null;
  heat_reduction_coefficient?: number | null;
  heat_is_direct?: boolean | null;
  heat_notes?: string | null;
  corp_name?: string | null;
  building_name?: string | null;
  apartment_id?: string | null;
  apartment_number?: string | number;
  rates?: Array<{ code: string; label?: string; value: number | null }>;
  heat_installation_date?: string | null;
  heat_removal_date?: string | null;
  heat_seal_status?: string | null;
  heat_operational_status?: string | null;
  heat_parent_device_id?: string | null;
  heat_archived?: boolean | null;
};

export type DevicesSearchResponse = {
  items: DeviceInDB[];
  total: number;
  page: number;
  page_size: number;
};

export type DevicesListFilters = {
  status?: string;
  system_type?: string;
  manufacturer?: string;
  serial_number?: string;
  dev_eui?: string;
  room_number?: string;
  suffix?: string;
};

export type DevicesListSort = {
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
};

export type DeviceStatusSummary = {
  total: number;
  ok: number;
  warn: number;
  error: number;
  new: number;
  online: number;
  offline: number;
  no_readings: number;
  readings_last_24h?: number;
  readings_last_7d?: number;
  readings_last_30d?: number;
};

export type DeviceLoRaWanStatus = {
  device_id: string;
  lorawan_enabled: boolean;
  lorawan_sync_status?: string | null;
  lorawan_last_sync_at?: string | null;
  lorawan_sync_error?: string | null;
  lorawan_signal_rssi?: number | null;
  lorawan_signal_snr?: number | null;
  lorawan_gateways_count?: number | null;
  dev_eui?: string | null;
  profile_mapping_found: boolean;
  tenant_binding_configured: boolean;
};

export type DeviceLoRaWanSyncLog = {
  id: string;
  device_id: string;
  action: string;
  status: string;
  request_payload?: Record<string, unknown> | null;
  response_payload?: Record<string, unknown> | null;
  error_message?: string | null;
  created_by_user_id?: string | null;
  created_at: string;
};

export type DeviceLoRaWanGatewayObservation = {
  id: string;
  uplink_event_id: string;
  gateway_id: string;
  gateway_name?: string | null;
  rssi?: number | null;
  snr?: number | null;
  channel?: number | null;
  context?: string | null;
  crc_status?: string | null;
  gw_time?: string | null;
  location_lat?: number | null;
  location_lon?: number | null;
  created_at: string;
};

export type DeviceLoRaWanUplinkEvent = {
  id: string;
  device_id: string;
  dev_eui: string;
  application_id?: string | null;
  external_tenant_id?: string | null;
  event_type?: string | null;
  uplink_id?: string | null;
  f_cnt?: number | null;
  confirmed?: boolean | null;
  received_at: string;
  ns_time?: string | null;
  gw_time?: string | null;
  frequency?: number | null;
  spreading_factor?: number | null;
  bandwidth?: number | null;
  code_rate?: string | null;
  gateway_count: number;
  best_rssi?: number | null;
  best_snr?: number | null;
  created_at: string;
  gateway_observations: DeviceLoRaWanGatewayObservation[];
};

export type DeviceLoRaWanDownlinkEvent = {
  id: string;
  device_id: string;
  dev_eui: string;
  application_id?: string | null;
  external_tenant_id?: string | null;
  event_type: string;
  f_cnt_down?: number | null;
  confirmed?: boolean | null;
  acked?: boolean | null;
  gateway_id?: string | null;
  correlation_key?: string | null;
  context?: string | null;
  selected_gateway_rssi?: number | null;
  selected_gateway_snr?: number | null;
  best_gateway_id?: string | null;
  best_gateway_rssi?: number | null;
  best_gateway_snr?: number | null;
  gateway_choice_problem?: boolean;
  received_at: string;
  ns_time?: string | null;
  created_at: string;
};

export type DeviceLoRaWanDiagnosticSummary = {
  device_id: string;
  events_count: number;
  latest_event?: DeviceLoRaWanUplinkEvent | null;
};

export type LoRaWanHealthSnapshot = {
  id: string;
  period_start: string;
  period_end: string;
  uplinks_count: number;
  rssi_avg?: number | null;
  rssi_min?: number | null;
  snr_avg?: number | null;
  snr_min?: number | null;
  last_seen_at?: string | null;
  health_status: string;
  notes?: string | null;
  reasons?: string[];
};

export type LoRaWanDeviceHealthSnapshot = LoRaWanHealthSnapshot & {
  device_id: string;
  apartment_id?: string | null;
  device_serial_number?: string | null;
  device_system_type?: string | null;
  device_dev_eui?: string | null;
  confirmed_uplinks_count: number;
  duplicate_fcnt_count: number;
  gateway_count_avg?: number | null;
  gateway_count_min?: number | null;
};

export type LoRaWanGatewayHealthSnapshot = LoRaWanHealthSnapshot & {
  gateway_id: string;
  gateway_name?: string | null;
  unique_devices_count: number;
  downlink_count: number;
  ack_count: number;
  gateway_record_id?: string | null;
  configuration_status: 'linked' | 'topic_only' | 'missing' | 'traffic_only';
  configuration_source?: 'gateway_id' | 'mac' | 'mqtt_topic' | null;
  statistics_source: 'live' | 'snapshot' | 'none';
  known_packets: number;
  unknown_packets: number;
  unidentified_packets: number;
};

export type LoRaWanGatewayDiagnosticUplink = {
  id: string;
  uplink_event_id: string;
  device_id: string;
  apartment_id?: string | null;
  device_serial_number?: string | null;
  device_system_type?: string | null;
  dev_eui: string;
  event_type?: string | null;
  received_at: string;
  f_cnt?: number | null;
  rssi?: number | null;
  snr?: number | null;
};

export type LoRaWanGatewayDiagnosticsDetail = {
  gateway?: LoRaWanGatewayHealthSnapshot | null;
  recent_uplinks: LoRaWanGatewayDiagnosticUplink[];
  recent_downlinks: DeviceLoRaWanDownlinkEvent[];
};

export type LoRaWanDiagnosticsOverview = {
  devices_total: number;
  devices_good: number;
  devices_warning: number;
  devices_no_data: number;
  gateways_total: number;
  gateways_warning: number;
  latest_period_end?: string | null;
  problem_devices: LoRaWanDeviceHealthSnapshot[];
  problem_gateways: LoRaWanGatewayHealthSnapshot[];
};

export type LoRaWanUnknownDeviceTraffic = {
  dev_eui: string;
  packets_count: number;
  gateway_count: number;
  gateway_ids: string[];
  first_seen_at: string;
  last_seen_at: string;
  best_rssi?: number | null;
  best_snr?: number | null;
  application_id?: string | null;
};

export type LoRaWanTrafficOverview = {
  period_start: string;
  period_end: string;
  packets_total: number;
  receptions_total: number;
  known_packets: number;
  unknown_packets: number;
  unidentified_packets: number;
  unknown_devices_total: number;
  gateways_active: number;
  unknown_devices: LoRaWanUnknownDeviceTraffic[];
};

export const devicesApi = {
  async getModelSuggestions(params: {
    building_id: string;
    system_type?: string;
    limit?: number;
  }): Promise<string[]> {
    const res = await apiClient.get<string[]>('/devices/model-suggestions', { params });
    return res.data;
  },

  async getByApartment(apartmentId: string): Promise<DeviceInDB[]> {
    const res = await apiClient.get<DeviceInDB[]>(`/devices/by-apartment/${apartmentId}`);
    return res.data;
  },

  async getByCorps(
    corpsId: string,
    params?: {
      history_date?: string;
      page?: number;
      page_size?: number;
      search?: string;
    } & DevicesListFilters &
      DevicesListSort
  ) {
    const res = await apiClient.get<{ items: DeviceInDB[]; total: number }>(
      `/devices/by-corps/${corpsId}`,
      { params }
    );
    return res.data;
  },
  async searchDevices(query: string, params?: { page?: number; page_size?: number; history_date?: string }) {
    const res = await apiClient.get<DevicesSearchResponse>('/devices/search', {
      params: { query, ...params },
    });
    return res.data;
  },
  async getStatusSummary(): Promise<DeviceStatusSummary> {
    const res = await apiClient.get<DeviceStatusSummary>('/devices/status-summary');
    return res.data;
  },
  async createDevice(data: DeviceCreate): Promise<DeviceInDB> {
    const res = await apiClient.post<DeviceInDB>('/devices/', data);
    return res.data;
  },
  async updateDevice(id: string, data: DeviceUpdate): Promise<DeviceInDB> {
    const res = await apiClient.put<DeviceInDB>(`/devices/${id}`, data);
    return res.data;
  },
  async assignApartment(id: string, data: { apartment_id: string }): Promise<DeviceInDB> {
    const res = await apiClient.post<DeviceInDB>(`/devices/${id}/assign-apartment`, data);
    return res.data;
  },
  async moveApartment(id: string, apartmentId: string): Promise<DeviceInDB> {
    const res = await apiClient.post<DeviceInDB>(`/devices/${id}/move-apartment`, {
      apartment_id: apartmentId,
    });
    return res.data;
  },
  async detachApartment(id: string): Promise<DeviceInDB> {
    const res = await apiClient.post<DeviceInDB>(`/devices/${id}/detach-apartment`);
    return res.data;
  },
  async assignNewApartment(
    id: string,
    data: {
      number: string;
      suffix?: string;
      floor_number?: string;
      section_number?: string;
      area?: string;
      owner_name?: string;
      create_building?: boolean;
      building_name?: string;
      building_address?: string;
    }
  ): Promise<DeviceInDB> {
    const res = await apiClient.post<DeviceInDB>(`/devices/${id}/assign-new-apartment`, data);
    return res.data;
  },
  async getLoRaWanStatus(id: string): Promise<DeviceLoRaWanStatus> {
    const res = await apiClient.get<DeviceLoRaWanStatus>(`/devices/${id}/lorawan/status`);
    return res.data;
  },
  async syncLoRaWan(id: string): Promise<DeviceLoRaWanStatus> {
    const res = await apiClient.post<DeviceLoRaWanStatus>(`/devices/${id}/lorawan/sync`);
    return res.data;
  },
  async disableLoRaWan(id: string): Promise<DeviceLoRaWanStatus> {
    const res = await apiClient.post<DeviceLoRaWanStatus>(`/devices/${id}/lorawan/disable`);
    return res.data;
  },
  async getLoRaWanLog(id: string): Promise<DeviceLoRaWanSyncLog[]> {
    const res = await apiClient.get<DeviceLoRaWanSyncLog[]>(`/devices/${id}/lorawan/log`);
    return res.data;
  },
  async getLoRaWanDiagnostics(id: string): Promise<DeviceLoRaWanDiagnosticSummary> {
    const res = await apiClient.get<DeviceLoRaWanDiagnosticSummary>(`/devices/${id}/lorawan/diagnostics`);
    return res.data;
  },
  async getLoRaWanUplinks(id: string, limit = 20): Promise<DeviceLoRaWanUplinkEvent[]> {
    const res = await apiClient.get<DeviceLoRaWanUplinkEvent[]>(`/devices/${id}/lorawan/uplinks`, {
      params: { limit },
    });
    return res.data;
  },
  async getLoRaWanDownlinks(id: string, limit = 20): Promise<DeviceLoRaWanDownlinkEvent[]> {
    const res = await apiClient.get<DeviceLoRaWanDownlinkEvent[]>(`/devices/${id}/lorawan/downlinks`, {
      params: { limit },
    });
    return res.data;
  },
  async getLoRaWanDiagnosticsOverview(limit = 1000, includeGood = false): Promise<LoRaWanDiagnosticsOverview> {
    const res = await apiClient.get<LoRaWanDiagnosticsOverview>('/devices/lorawan/diagnostics/overview', {
      params: { limit, include_good: includeGood },
    });
    return res.data;
  },
  async getLoRaWanTrafficOverview(hours = 24, unknownLimit = 100): Promise<LoRaWanTrafficOverview> {
    const res = await apiClient.get<LoRaWanTrafficOverview>('/devices/lorawan/diagnostics/traffic', {
      params: { hours, unknown_limit: unknownLimit },
    });
    return res.data;
  },
  async listLoRaWanDiagnosticGateways(limit = 500): Promise<LoRaWanGatewayHealthSnapshot[]> {
    const res = await apiClient.get<LoRaWanGatewayHealthSnapshot[]>('/devices/lorawan/diagnostics/gateways', {
      params: { limit },
    });
    return res.data;
  },
  async getLoRaWanGatewayDiagnostics(gatewayId: string, limit = 30): Promise<LoRaWanGatewayDiagnosticsDetail> {
    const res = await apiClient.get<LoRaWanGatewayDiagnosticsDetail>(
      `/devices/lorawan/diagnostics/gateways/${encodeURIComponent(gatewayId)}`,
      { params: { limit } }
    );
    return res.data;
  },
  async exportLoRaWanDiagnostics(limit = 10000): Promise<Blob> {
    const res = await apiClient.get('/devices/lorawan/diagnostics/export.csv', {
      params: { limit },
      responseType: 'blob',
    });
    return res.data;
  },
  async getBusConnection(id: string): Promise<DeviceBusConnection | null> {
    const res = await apiClient.get<DeviceBusConnection | null>(`/devices/${id}/bus-connection`);
    return res.data ?? null;
  },
  async listBusProfiles(): Promise<BusDeviceProfile[]> {
    const res = await apiClient.get<BusDeviceProfile[]>('/devices/bus-profiles');
    return res.data;
  },
  async getBusConnectionOptions(id: string): Promise<DeviceBusUspdOption[]> {
    const res = await apiClient.get<DeviceBusUspdOption[]>(`/devices/${id}/bus-connection/options`);
    return res.data;
  },
  async suggestBusAddress(id: string, lineId: string): Promise<string> {
    const res = await apiClient.get<{ bus_address: string }>(`/devices/${id}/bus-connection/suggest-address`, {
      params: { line_id: lineId },
    });
    return res.data.bus_address;
  },
  async upsertBusConnection(id: string, payload: DeviceBusConnectionUpsert): Promise<DeviceBusConnection> {
    const res = await apiClient.put<DeviceBusConnection>(`/devices/${id}/bus-connection`, payload);
    return res.data;
  },
  async closeBusConnection(id: string, reason: string): Promise<DeviceBusConnection> {
    const res = await apiClient.post<DeviceBusConnection>(`/devices/${id}/bus-connection/close`, { reason });
    return res.data;
  },
  async pollViaEdgeAgent(id: string): Promise<DeviceBusPollResult> {
    const res = await apiClient.post<DeviceBusPollResult>(`/devices/${id}/bus-connection/poll`);
    return res.data;
  },
  async getEdgeAgentPollResult(id: string, commandId: string): Promise<DeviceBusPollResult> {
    const res = await apiClient.get<DeviceBusPollResult>(
      `/devices/${id}/bus-connection/poll/${encodeURIComponent(commandId)}`
    );
    return res.data;
  },
};

export type DeviceBusPollResult = {
  command_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  message: string;
  agent_name?: string | null;
  data?: Record<string, unknown> | null;
};

export type DeviceBusConnection = {
  id: string;
  device_id: string;
  line_id: string;
  transport_type: string;
  bus_address: string;
  profile_key?: string | null;
  profile_version?: number | null;
  channel_number?: number | null;
  active_from: string;
  active_to?: string | null;
  close_reason?: string | null;
  notes?: string | null;
  gateway_id?: string | null;
  gateway_name: string;
  line_number?: string | null;
  line_name?: string | null;
  is_bus: boolean;
  is_pulse: boolean;
};

export type BusDeviceProfile = {
  key: string;
  version: number;
  name: string;
  manufacturer?: string | null;
  transport: string;
  driver_key: string;
  addressing_mode: string;
  discovery_mode: string;
  status: 'draft' | 'diagnostic' | 'verified' | 'disabled' | string;
  default_baud_rate?: number | null;
  default_parity?: string | null;
  default_stop_bits?: number | null;
  address_hint?: string | null;
  address_suggestion?: string | null;
  supports_readings: boolean;
};

export type DeviceBusLineOption = {
  id: string;
  line_number: string;
  name?: string | null;
  transport_type: string;
  protocol?: string | null;
  device_count: number;
};

export type DeviceBusUspdOption = {
  id: string;
  name: string;
  host?: string | null;
  lines: DeviceBusLineOption[];
};

export type DeviceBusConnectionUpsert = {
  line_id: string;
  transport_type: 'rs485' | 'mbus';
  bus_address: string;
  profile_key?: string;
  reason?: string;
  notes?: string;
};
